import { useState } from 'react';

import HubRoundedIcon from '@mui/icons-material/HubRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Stack } from '@mui/material';

import { testExternalPricingEngineConnection } from '../../api/pricingSettingsApi';
import { usePricingSettings, useUpdatePricingSettings } from '../../hooks';
import { useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type { EngineMode, UpdatePricingSettingsRequest } from '../../model/settingsTypes';
import {
  FeedbackSnackbar,
  FieldRow,
  MaskedCredentialField,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TestConnectionButton,
  TextInputField,
} from '../components';
import { ENGINE_MODE_OPTIONS } from '../options';

/** Pricing / promotion engine configuration and external-engine connection. */
export function PricingSettingsPage() {
  const { canView, canManage, canManageCredentials } = useDomainPermissions('pricing');
  const query = usePricingSettings();
  const update = useUpdatePricingSettings();
  const form = useSettingsForm(query.data);
  const [engineSecret, setEngineSecret] = useState('');
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const dirty = form.dirty || engineSecret.trim() !== '';

  const reset = () => {
    form.reset();
    setEngineSecret('');
  };

  const handleSave = () => {
    const draft = form.draft;
    if (!draft) {
      return;
    }
    const body: UpdatePricingSettingsRequest = {
      version: draft.version,
      pricingEngineMode: draft.pricingEngineMode,
      promotionEngineMode: draft.promotionEngineMode,
      roundingRule: draft.roundingRule,
      discountStacking: draft.discountStacking,
      promoCodesEnabled: draft.promoCodesEnabled,
      deliveryPriceCalculation: draft.deliveryPriceCalculation,
      paymentFeeCalculation: draft.paymentFeeCalculation,
      externalEngine: {
        baseUrl: draft.externalEngine?.baseUrl ?? null,
        ...(engineSecret.trim() !== '' ? { secret: engineSecret } : {}),
      },
    };
    update.mutate(body, {
      onSuccess: () => {
        setEngineSecret('');
        success('Pricing settings saved.');
      },
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title="Pricing settings"
      subtitle="Pricing/promotion engine mode, rounding rules and external engine."
      icon={<SellRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title="Engines" icon={<SellRoundedIcon />}>
              <SelectField
                label="Pricing engine mode"
                value={draft.pricingEngineMode}
                options={ENGINE_MODE_OPTIONS}
                onChange={(value) => form.setField('pricingEngineMode', value as EngineMode)}
                disabled={disabled}
              />
              <SelectField
                label="Promotion engine mode"
                value={draft.promotionEngineMode}
                options={ENGINE_MODE_OPTIONS}
                onChange={(value) => form.setField('promotionEngineMode', value as EngineMode)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title="Calculation rules" icon={<TuneRoundedIcon />}>
              <TextInputField
                label="Rounding rule"
                value={draft.roundingRule}
                onChange={(value) => form.setField('roundingRule', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Discount stacking"
                description="Allow multiple discounts to apply together."
                checked={draft.discountStacking}
                onChange={(value) => form.setField('discountStacking', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Promo codes"
                checked={draft.promoCodesEnabled}
                onChange={(value) => form.setField('promoCodesEnabled', value)}
                disabled={disabled}
              />
              <TextInputField
                label="Delivery price calculation"
                value={draft.deliveryPriceCalculation}
                onChange={(value) => form.setField('deliveryPriceCalculation', value)}
                disabled={disabled}
              />
              <TextInputField
                label="Payment fee calculation"
                value={draft.paymentFeeCalculation}
                onChange={(value) => form.setField('paymentFeeCalculation', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard
              title="External engine"
              description="Configure and verify an external pricing engine."
              icon={<HubRoundedIcon />}
            >
              <TextInputField
                label="Base URL"
                type="url"
                value={draft.externalEngine?.baseUrl ?? ''}
                onChange={(value) =>
                  form.setField('externalEngine', {
                    baseUrl: value || null,
                    credential: draft.externalEngine?.credential ?? null,
                  })
                }
                disabled={disabled}
                placeholder="https://pricing.example.com"
              />
              <MaskedCredentialField
                label="Engine credential"
                description="Secret used to authenticate with the external engine."
                credential={draft.externalEngine?.credential ?? null}
                secret={engineSecret}
                onSecretChange={setEngineSecret}
                disabled={!canManageCredentials}
              />
              <FieldRow
                label="Connection"
                description="Verify the external engine is reachable."
                alignTop
              >
                <TestConnectionButton
                  run={testExternalPricingEngineConnection}
                  disabled={!canManage}
                />
              </FieldRow>
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
